import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'
/** API */
import ProductPreservesApi from 'api/setting/default-products/product-preserve.api'

/** UTILS || SLICE */
import { submitModal } from 'app/slice/modal.slice'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { Box, Grid, InputAdornment, Typography } from '@mui/material'
import HeaderCard from 'component/CardCustom/HeaderCard'
import { notiError, notiSuccess } from 'component/notifications/notifications'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import MainButton from 'new-components/buttons/main-button'
import SwitchButton from 'new-components/buttons/switch-button'
import SelectInput from 'new-components/inputs/select-input'
import TextInput from 'new-components/inputs/text-input'
import AlertModal from 'new-components/modal/alert-modal'
import PageModal from 'new-components/modal/page-modal'
import SnackbarComponent from 'new-components/snackbar/snackbar-component'

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}

const initStateErrorMessage = {
  PRODUCT_PRESERVE_NAME: ``,
  IS_DUPLICATE_PRODUCT_PRESERVE_NAME: ``,
  PRODUCT_PRESERVE_NAME_STRING_BASE: ``,
  PRODUCT_PRESERVE_NAME_STRING_EMPTY: ``,
  PRODUCT_PRESERVE_NAME_MAXIMUM_LENGTH: ``,
  PRODUCT_PRESERVE_NAME_ANY_REQUIRED: ``,

  PRODUCT_PRESERVE_NAME_EN: ``,
  IS_DUPLICATE_PRODUCT_PRESERVE_NAME_EN: ``,
  PRODUCT_PRESERVE_NAME_EN_STRING_BASE: ``,
  PRODUCT_PRESERVE_NAME_EN_STRING_EMPTY: ``,
  PRODUCT_PRESERVE_NAME_EN_MAXIMUM_LENGTH: ``,
  PRODUCT_PRESERVE_NAME_EN_ANY_REQUIRED: ``
}
export default function ProductPreserves() {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [productPreserves, setProductPreserves] = useState([])

  const [productPreserveId, setProductPreserveId] = useState(Number)
  const [productPreserveName, setProductPreserveName] = useState('')
  const [productPreserveNameEn, setProductPreserveNameEn] = useState('')
  const [productPreserveStatus, setProductPreserveStatus] = useState('')

  const [errorMessage, setErrorMessage] = useState(initStateErrorMessage)

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')
  const [disabledSubmit, setDisabledSubmit] = useState(false)
  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')
  const [nameEn, setNameEn] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<any>("success")
  const [labelSnack, setLabelSnack] = useState('')

  const [isOpenPageModal, setIsOpenPageModal] = useState(false)

  const loadData = async () => {
    try {
      const resp = await ProductPreservesApi.findAll({
        group: 'PRODUCT',
        page: page,
        pageLimit: pageLimit,
        search: search,
        sortBy: sortBy,
        sortType: sortType,
        status: status === 'ALL' ? '' : status
      })
      if (resp.status === 200) {
        setRowCount(resp.headers['x-total'])
        setProductPreserves(resp.data)
      } else {
        setProductPreserves([])
      }
    } catch (error) {
      notiError('ERROR')
    }
  }

  useEffect(() => {
    loadData()
  }, [page, pageLimit, sortBy, sortType])

  const submit = () => {
    if (!productPreserveName) return setErrorMessage({ ...errorMessage, ...{ PRODUCT_PRESERVE_NAME: t('PRODUCT_PRESERVE.MESSAGE.PRODUCT_PRESERVE_NAME') } })

    dispatch(submitModal())
    setDisabledSubmit(true)
    ProductPreservesApi.create({ productPreserveName, productPreserveNameEn, group: 'PRODUCT' })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 201) {
          notiSuccess(t('PRODUCT_PRESERVE.MESSAGE.SUCCESS.CREATE'), '', null, null)
          resetForm()
          loadData()
          setIsOpenPageModal(false)
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`PRODUCT_PRESERVE.MESSAGE.${err.message}`) } })
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`PRODUCT_PRESERVE.MESSAGE.${err.message}`) } })
      }).finally(() => setDisabledSubmit(false))
  }

  const submitEdit = () => {
    if (!productPreserveName) return setErrorMessage({ ...errorMessage, ...{ PRODUCT_PRESERVE_NAME: t(`PRODUCT_PRESERVE.MESSAGE.PRODUCT_PRESERVE_NAME`) } })

    dispatch(submitModal())
    setDisabledSubmit(true)
    ProductPreservesApi.update(productPreserveId, { productPreserveName, productPreserveNameEn, status: productPreserveStatus, group: 'PRODUCT' })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 200) {
          notiSuccess(t(`PRODUCT_PRESERVE.MESSAGE.SUCCESS.UPDATE`), '', null, null)
          resetForm()
          loadData()
          setIsOpenPageModal(false)
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`PRODUCT_PRESERVE.MESSAGE.${err.message}`) } })
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`PRODUCT_PRESERVE.MESSAGE.${err.message}`) } })
      }).finally(() => setDisabledSubmit(false))
  }

  const clearErrorMessage = () => {
    setErrorMessage(initStateErrorMessage)
  }

  const resetForm = () => {
    setProductPreserveId(0)
    setProductPreserveName('')
    setProductPreserveNameEn('')
    setProductPreserveStatus('')
    clearErrorMessage()
    setIsOpenPageModal(false)
  }

  const onActive = () => {
    if (id) {
      ProductPreservesApi.updateStatus(id, statusAlertModal).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('PRODUCT_PRESERVE.MESSAGE.SUCCESS.UPDATE_STATUS'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('PRODUCT_PRESERVE.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onDelete = () => {
    if (id) {
      ProductPreservesApi.remove(id).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('PRODUCT_PRESERVE.MESSAGE.SUCCESS.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('PRODUCT_PRESERVE.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onEdit = (data: any) => {
    const { productPreserveId: pdPreserveId, productPreserveName: pdPreserveName, productPreserveNameEn: pdPreserveNameEn, status: pdPreserveStatus } = data
    setProductPreserveId(pdPreserveId)
    setProductPreserveName(pdPreserveName)
    setProductPreserveNameEn(pdPreserveNameEn)
    setProductPreserveStatus(pdPreserveStatus)
    setIsOpenPageModal(true)
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }

  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }
  const headCells = [
    { id: 'no', disablePadding: false, label: '#' },
    { id: 'productPreserveName', disablePadding: false, label: t('PRODUCT_PRESERVE.TABLE.CELL.PRODUCT_PRESERVE_NAME') },
    { id: 'productPreserveNameEn', disablePadding: false, label: t('PRODUCT_PRESERVE.TABLE.CELL.PRODUCT_PRESERVE_NAME_EN') },
    { id: 'status', disablePadding: false, label: t('PRODUCT_PRESERVE.TABLE.CELL.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('PRODUCT_PRESERVE.TABLE.CELL.UPDATED_BY') },
    { id: 'action', disablePadding: false, label: 'Action' }
  ]

  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { productPreserveId: pdPreserveId, productPreserveName: pdPreserveName, productPreserveNameEn: pdPreserveNameEn, updatedBy, updatedAt, status: pdPreserveStatus } = objData
    const statusBtnActive = pdPreserveStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = pdPreserveStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled
    const objRenderData = {
      key: pdPreserveId,
      id: pdPreserveId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        { option: 'TEXT', align: 'left', label: pdPreserveName },
        { option: 'TEXT', align: 'left', label: pdPreserveNameEn },
        { option: 'STATUS', align: 'center', label: pdPreserveStatus },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        }
      ]
    }
    return (
      <TableRowCommon
        {...objRenderData}
        onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
        oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
        onedit={() => onEdit(objRenderData.obj)}
        ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
      />
    )
  }

  const handleSubmitModal = (id: number, status: string, obj?: any) => {
    setStatusAlertModal(status)
    setId(id)
    setIsOpenAlertModal(true)
    setName(obj.productPreserveName)
    setNameEn(obj.productPreserveNameEn)
  }

  return (
    <Box>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`PRODUCT_PRESERVE.ALERT.${statusAlertModal === 'DELETE' ? 'TITLE_DELETE' : 'TITLE_ACTIVE'}`)}
        message={t(`PRODUCT_PRESERVE.ALERT.${statusAlertModal === 'DELETE' ? 'CONFIRM_DELETE_' : 'CONFIRM_STATUS'}`, { productPreserveName: name, productPreserveNameEn: nameEn && `(${nameEn})`, status: t(`PRODUCT_PRESERVE.ALERT.${statusAlertModal}`) })}
        type={statusAlertModal === 'DELETE' ? 'error' : 'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: statusAlertModal === 'DELETE' ? onDelete : onActive, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 32px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <HeaderCard text={t('PRODUCT_PRESERVE.TITLE')} />
      <Box paddingX="24px" paddingTop="16px">
        <Grid container spacing="16px">
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <TextInput
              label={t('PRODUCT_PRESERVE.SEARCH')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
              }}
              fullWidth
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end" >
                    {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                      <FontAwesomeIcon
                        icon={faTimes}
                        onClick={() => {
                          setSearch('');
                        }}
                        style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              onChange={(event) => {
                const value = event.target.value
                if (value) {
                  handleChangeStatus(value)
                }
              }}
              options={[
                { value: 'ALL', name: t('STATUS.ALL') },
                { value: 'ACTIVE', name: t('STATUS.ACTIVE') },
                { value: 'INACTIVE', name: t('STATUS.INACTIVE') }
              ]}
              label={t('STATUS.LABEL')}
              value={status}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} xl={8}>
            <Box display={'flex'} justifyContent={'space-between'}>
              <MainButton
                title={t('INPUT.SEARCH')}
                type='primary'
                variant='contained'
                style={{ width: '120px' }}
                onClick={() => {
                  setPage(1)
                  loadData()
                }} />

              <MainButton
                title={t('PRODUCT_PRESERVE.BUTTON.ADD')}
                disabled={permissions.isCreate.disabled}
                type='primary'
                variant='contained'
                onClick={() => setIsOpenPageModal(true)}
                startIcon={<AddCircleIcon />}
              />
            </Box>
          </Grid>
        </Grid>
        <Box paddingTop="24px">
          <TableCustom
            page={page}
            pageLimit={pageLimit}
            sortType={sortType}
            sortBy={sortBy}
            onSort={onRequestSort}
            setPageLimit={handleChangeRowsPerPage}
            setPage={handleChangePage}
            rowCount={rowCount}
            headCells={headCells}
            rowsData={productPreserves.map((val, i) => {
              return renderData(val, i)
            })}
          />
        </Box>
        <PageModal
          isOpen={isOpenPageModal}
          title={productPreserveId ? t('PRODUCT_PRESERVE.TITLE_UPDATE') : t('PRODUCT_PRESERVE.TITLE_CREATE')}
          size='XS'
          handleClose={resetForm}
          onCancel={{ onClick: resetForm, title: t('PRODUCT_PRESERVE.BUTTON.CANCEL') }}
          onSubmit={{ onClick: productPreserveId ? submitEdit : submit, title: t('PRODUCT_PRESERVE.BUTTON.SAVE'), disabled: disabledSubmit }}
        >
          <Box display="flex" flexDirection="column" gap="24px">
            {productPreserveId ? <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography>{t('INPUT.LABEL_STATUS')}</Typography>
              <SwitchButton onChange={() => setProductPreserveStatus(productPreserveStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')} checked={productPreserveStatus === 'ACTIVE' ? true : false} />
            </Box> : ''}
            <TextInput
              onChange={(event) => {
                setProductPreserveName(event.target.value)
                clearErrorMessage()
              }}
              value={productPreserveName}
              label={t('PRODUCT_PRESERVE.INPUT.PRODUCT_PRESERVE_NAME')}
              helperText={
                errorMessage.PRODUCT_PRESERVE_NAME ||
                errorMessage.IS_DUPLICATE_PRODUCT_PRESERVE_NAME ||
                errorMessage.PRODUCT_PRESERVE_NAME_STRING_BASE ||
                errorMessage.PRODUCT_PRESERVE_NAME_STRING_EMPTY ||
                errorMessage.PRODUCT_PRESERVE_NAME_MAXIMUM_LENGTH ||
                errorMessage.PRODUCT_PRESERVE_NAME_ANY_REQUIRED
              }
              required
            />
            <TextInput
              onChange={(event) => {
                setProductPreserveNameEn(event.target.value)
                clearErrorMessage()
              }}
              value={productPreserveNameEn}
              helperText={
                errorMessage.PRODUCT_PRESERVE_NAME_EN ||
                errorMessage.IS_DUPLICATE_PRODUCT_PRESERVE_NAME_EN ||
                errorMessage.PRODUCT_PRESERVE_NAME_EN_STRING_BASE ||
                errorMessage.PRODUCT_PRESERVE_NAME_EN_STRING_EMPTY ||
                errorMessage.PRODUCT_PRESERVE_NAME_EN_MAXIMUM_LENGTH ||
                errorMessage.PRODUCT_PRESERVE_NAME_EN_ANY_REQUIRED
              }
              label={t('PRODUCT_PRESERVE.INPUT.PRODUCT_PRESERVE_NAME_EN')}
            />
          </Box>
        </PageModal>
      </Box>
    </Box>
  )
}
